/**
 * client/net/transport.ts — WsTransport (online), WorkerTransport (single player), LagSimulator.
 * OWNER: NETCODE agent. [NET §9, §7.5]
 *
 * Reconnect support: WsTransport can `reopen()` a fresh socket to the same URL with the same
 * callbacks (the session retries every 1 s for 30 s with its reconnect token, NET §10).
 */
import type { Transport, WireData } from '@shared/protocol/transport';
import { fetchShared } from './assets-fetch';

/** A transport that can open a new underlying connection after a drop. */
export interface ReopenableTransport extends Transport {
  /** False when the wrapped transport cannot reconnect (e.g. LagSimulator around a Worker). */
  readonly canReopen: boolean;
  reopen(): void;
  onOpen(cb: () => void): void;
  readonly isOpen: boolean;
}
export function isReopenable(t: Transport): t is ReopenableTransport {
  return (t as Partial<ReopenableTransport>).canReopen === true;
}

export class WsTransport implements ReopenableTransport {
  readonly reliable = true;
  readonly canReopen = true;
  private ws!: WebSocket;
  private msgCb: (d: WireData) => void = () => {};
  private closeCb: (r: string) => void = () => {};
  private openCb: () => void = () => {};
  private queue: WireData[] = [];
  private closedByUs = false;

  constructor(private readonly url: string) {
    this.open();
  }

  private open(): void {
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.binaryType = 'arraybuffer';
    ws.onopen = () => {
      if (ws !== this.ws) return;
      for (const d of this.queue) ws.send(d);
      this.queue = [];
      this.openCb();
    };
    ws.onmessage = (e) => { if (ws === this.ws) this.msgCb(e.data as WireData); };
    ws.onclose = (e) => { if (ws === this.ws) this.closeCb(this.closedByUs ? 'left' : e.reason || `connection closed (${e.code})`); };
  }

  get isOpen(): boolean { return this.ws.readyState === WebSocket.OPEN; }

  reopen(): void {
    if (this.closedByUs) return;
    try { this.ws.onclose = null; this.ws.close(); } catch { /* already closed */ }
    this.queue = [];
    this.open();
  }

  send(data: WireData): void {
    if (this.ws.readyState === WebSocket.OPEN) this.ws.send(data);
    else if (this.ws.readyState === WebSocket.CONNECTING && typeof data === 'string') this.queue.push(data); // control only; stale cmds are useless
  }
  onMessage(cb: (d: WireData) => void): void { this.msgCb = cb; }
  onClose(cb: (r: string) => void): void { this.closeCb = cb; }
  onOpen(cb: () => void): void { this.openCb = cb; }
  close(): void { this.closedByUs = true; this.ws.close(1000, 'leave'); }
}

/** Default ws URL: same origin /ws (Vite dev proxies it to the Node server). */
export function defaultWsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/ws`;
}

/**
 * Talks to worker/sim-worker.ts. Binary messages are transferred (zero copy).
 * The worker loads its assets THROUGH the page (`{kind:'asset', id, path}` -> `{kind:'asset', id,
 * data | error}`), so the page and the sim share one download of collision.bin (assets-fetch.ts).
 */
export class WorkerTransport implements Transport {
  readonly reliable = true;
  readonly worker: Worker;
  private msgCb: (d: WireData) => void = () => {};
  private closeCb: (r: string) => void = () => {};

  constructor() {
    this.worker = new Worker(new URL('../../worker/sim-worker.ts', import.meta.url), { type: 'module', name: 'sim' });
    this.worker.onmessage = (e: MessageEvent<{ kind: string; data?: WireData; text?: string; id?: number; path?: string }>) => {
      const m = e.data;
      if (m.kind === 'msg' && m.data !== undefined) this.msgCb(m.data);
      else if (m.kind === 'asset' && typeof m.path === 'string') {
        const id = m.id;
        fetchShared('/assets/' + m.path).then(
          (data) => this.worker.postMessage({ kind: 'asset', id, data }, [data]),
          (err: unknown) => this.worker.postMessage({ kind: 'asset', id, error: String(err) }),
        );
      }
      else if (m.kind === 'log') console.log(`[sim] ${m.text}`);
      else if (m.kind === 'closed') this.closeCb('worker closed');
    };
    this.worker.onerror = (e) => { console.error('[sim worker]', e.message); this.closeCb(`worker error: ${e.message}`); };
  }
  send(data: WireData): void {
    if (typeof data === 'string') this.worker.postMessage({ kind: 'msg', data });
    else this.worker.postMessage({ kind: 'msg', data }, [data]);
  }
  setPaused(paused: boolean): void { this.worker.postMessage({ kind: 'pause', paused }); }
  onMessage(cb: (d: WireData) => void): void { this.msgCb = cb; }
  onClose(cb: (r: string) => void): void { this.closeCb = cb; }
  close(): void { this.worker.terminate(); }
}

export interface LagParams { lag: number; jitter: number; loss: number }

/**
 * ?lag=120&jitter=20&loss=0.02 [NET §7.5] — delays each direction by lag/2 ± jitter. On a reliable
 * transport "loss" does not drop anything: a lost segment delays that message by one extra RTT and,
 * because order is kept, everything behind it too (TCP head-of-line blocking, NET pitfall 10).
 */
export class LagSimulator implements ReopenableTransport {
  readonly reliable: boolean;
  private msgCb: (d: WireData) => void = () => {};
  private readonly up = new DelayQueue((d) => this.inner.send(d));
  private readonly down = new DelayQueue((d) => this.msgCb(d));
  readonly canReopen: boolean;
  constructor(private readonly inner: Transport, private readonly p: LagParams) {
    this.reliable = inner.reliable;
    this.canReopen = isReopenable(inner);
    inner.onMessage((d) => this.down.push(d, this.delay()));
  }
  /** One-way delay for the next message: lag/2 ± jitter (+ one RTT on a "lost" segment). */
  private delay(): number {
    let d = this.p.lag / 2 + (Math.random() * 2 - 1) * this.p.jitter;
    if (this.p.loss > 0 && Math.random() < this.p.loss) d += this.p.lag; // HOL blocking: one extra RTT
    return Math.max(0, d);
  }
  send(data: WireData): void { this.up.push(data, this.delay()); }
  get isOpen(): boolean { return isReopenable(this.inner) ? this.inner.isOpen : true; }
  reopen(): void {
    if (!isReopenable(this.inner)) return;
    this.up.clear(); // in-flight messages belonged to the dead connection
    this.down.clear();
    this.inner.reopen();
  }
  onOpen(cb: () => void): void { if (isReopenable(this.inner)) this.inner.onOpen(cb); }
  onMessage(cb: (d: WireData) => void): void { this.msgCb = cb; }
  onClose(cb: (r: string) => void): void { this.inner.onClose(cb); }
  close(): void { this.inner.close(); }
}

/**
 * Strict FIFO with per-message delivery times (a message is never delivered before the one ahead
 * of it, like TCP). One timer for the head: separate setTimeouts per message can reorder messages
 * due at the same moment, because browsers round timer delays.
 */
class DelayQueue {
  private readonly q: { at: number; d: WireData }[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private last = 0;
  constructor(private readonly deliver: (d: WireData) => void) {}
  push(d: WireData, delayMs: number): void {
    const at = Math.max(this.last, performance.now() + delayMs);
    this.last = at;
    this.q.push({ at, d });
    if (!this.timer) this.arm();
  }
  clear(): void {
    this.q.length = 0;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.last = 0;
  }
  private arm(): void {
    const head = this.q[0];
    if (!head) { this.timer = null; return; }
    this.timer = setTimeout(() => this.flush(), Math.max(0, head.at - performance.now()));
  }
  private flush(): void {
    const now = performance.now() + 0.5; // timers fire up to ~1 ms early/late after rounding
    let n = 0;
    while (n < this.q.length && this.q[n].at <= now) n++;
    const due = this.q.splice(0, Math.max(1, n));
    this.timer = null;
    for (const m of due) this.deliver(m.d);
    if (!this.timer) this.arm();
  }
}

export function lagParamsFromUrl(): LagParams | null {
  const q = new URLSearchParams(location.search);
  const lag = Number(q.get('lag') ?? 0), jitter = Number(q.get('jitter') ?? 0), loss = Number(q.get('loss') ?? 0);
  return lag || jitter || loss ? { lag, jitter, loss } : null;
}
